import React, { Suspense, lazy } from "react";
import "@/App.css";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { ConsentProvider } from "@/context/ConsentContext";
import { UIProvider } from "@/context/UIContext";
import { useSeo } from "@/hooks/useSeo";

// Above-the-fold: load eagerly for the fastest first paint.
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { Services } from "@/components/site/Services";
import { WhatsAppWidget } from "@/components/site/WhatsAppWidget";
import { CookieConsent } from "@/components/site/CookieConsent";
import { LegalDialogs } from "@/components/site/LegalDialogs";
import { SectionSkeleton } from "@/components/site/SectionSkeleton";

// Below-the-fold: code-split into separate chunks that stream in as needed.
const Gallery = lazy(() =>
  import("@/components/site/Gallery").then((m) => ({ default: m.Gallery }))
);
const Pricing = lazy(() =>
  import("@/components/site/Pricing").then((m) => ({ default: m.Pricing }))
);
const Reviews = lazy(() =>
  import("@/components/site/Reviews").then((m) => ({ default: m.Reviews }))
);
const Trust = lazy(() =>
  import("@/components/site/Trust").then((m) => ({ default: m.Trust }))
);
const Location = lazy(() =>
  import("@/components/site/Location").then((m) => ({ default: m.Location }))
);
const Hours = lazy(() =>
  import("@/components/site/Hours").then((m) => ({ default: m.Hours }))
);
const Instagram = lazy(() =>
  import("@/components/site/Instagram").then((m) => ({ default: m.Instagram }))
);
const CTABand = lazy(() =>
  import("@/components/site/CTABand").then((m) => ({ default: m.CTABand }))
);
const Footer = lazy(() =>
  import("@/components/site/Footer").then((m) => ({ default: m.Footer }))
);

// Wrap each lazy section so it reveals independently with an elegant skeleton.
function LazySection({ children, minHeight }) {
  return <Suspense fallback={<SectionSkeleton minHeight={minHeight} />}>{children}</Suspense>;
}

function SiteContent() {
  const { lang } = useLanguage();
  useSeo(lang);

  return (
    <div className="App min-h-screen bg-background text-foreground antialiased">
      <Header />
      <main>
        <Hero />
        <Services />
        <LazySection minHeight={720}>
          <Pricing />
        </LazySection>
        <LazySection minHeight={640}>
          <Gallery />
        </LazySection>
        <LazySection minHeight={420}>
          <Reviews />
        </LazySection>
        <LazySection minHeight={360}>
          <Trust />
        </LazySection>
        <LazySection minHeight={520}>
          <Location />
        </LazySection>
        <LazySection minHeight={300}>
          <Hours />
        </LazySection>
        <LazySection minHeight={480}>
          <Instagram />
        </LazySection>
        <LazySection minHeight={280}>
          <CTABand />
        </LazySection>
      </main>
      <Suspense fallback={<div className="h-64 bbm-skeleton" />}>
        <Footer />
      </Suspense>
      <WhatsAppWidget />
      <CookieConsent />
      <LegalDialogs />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ConsentProvider>
        <UIProvider>
          <SiteContent />
        </UIProvider>
      </ConsentProvider>
    </LanguageProvider>
  );
}

export default App;
